import { useQuery, gql } from '@apollo/client'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { Code, CalendarToday, GitHub, CloudDone } from '@mui/icons-material'

const GET_VERSION_INFO = gql`
  query GetVersionInfo {
    versionInfo {
      commit
      commitShort
      branch
      commitDate
      buildDate
      version
      environment
      instanceName
      sprintNumber
      sprintName
      currentRelease {
        version
        status
        environment
        deployedDate
        sprintNumber
      }
    }
  }
`

function VersionFooter() {
  const { data, loading, error } = useQuery(GET_VERSION_INFO, {
    fetchPolicy: 'cache-and-network',
  })

  const versionInfo = data?.versionInfo || {}
  const {
    commitShort = 'unknown',
    branch = 'unknown',
    buildDate,
    version = '0.0.0',
    environment = 'DEVELOPMENT',
    instanceName = 'Flight Demo',
    sprintNumber = 0,
    sprintName = 'No active sprint',
    currentRelease,
  } = versionInfo

  // Determine environment chip color
  const getEnvironmentColor = (env) => {
    if (env === 'PRODUCTION') return 'error'
    if (env === 'STAGING') return 'warning'
    return 'default'
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        px: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ width: '100%' }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
            Sprint {sprintNumber}:
          </Typography>
          <Chip
            icon={<Code sx={{ fontSize: 10 }} />}
            label={`${commitShort} (${branch})`}
            size="small"
            variant="outlined"
            sx={{ height: 14, fontSize: '0.6rem', '& .MuiChip-label': { py: 0, px: 0.5 } }}
          />
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
            Environment:
          </Typography>
          <Chip
            label={environment}
            size="small"
            color={getEnvironmentColor(environment)}
            sx={{ height: 14, fontSize: '0.6rem', '& .MuiChip-label': { py: 0, px: 0.5 } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1, ml: 1 }}>
            Instance:
          </Typography>
          <Chip
            label={instanceName}
            size="small"
            variant="outlined"
            sx={{ height: 14, fontSize: '0.6rem', '& .MuiChip-label': { py: 0, px: 0.5 } }}
          />
        </Stack>

        {currentRelease && (
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
              Release:
            </Typography>
            <Chip
              label={currentRelease.version}
              size="small"
              color="primary"
              sx={{ height: 14, fontSize: '0.6rem', '& .MuiChip-label': { py: 0, px: 0.5 } }}
            />
          </Stack>
        )}

        <Stack direction="row" spacing={0.5} alignItems="center">
          <CalendarToday sx={{ fontSize: 10 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
            Last Update:
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
            {buildDate ? new Date(buildDate).toLocaleDateString() : 'N/A'}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  )
}

export default VersionFooter
